import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Row,
    Col,
    Button,
    Form,
    Switch,
    InputNumber,
    Input,
    DatePicker,
    ConfigProvider,
    Table,
    Checkbox,
    Radio,
    Divider,
} from 'antd';
import {
    CalendarOutlined,
    SaveOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getGoodPractice, createOrUpdateGoodPractice } from '../../core/center-accreditation/centerAccreditationApi';

import './ScGoodPractices.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Title, Text } = Typography;
const { TextArea } = Input;

// Default dissemination forms
const defaultDisseminationForms = [
    { code: 'konferencija', title: 'Konferencijos', events: 0, participants: 0 },
    { code: 'apskriti_stalai', title: 'Apskriti stalai, diskusijos', events: 0, participants: 0 },
    { code: 'gerosios_patirties_leidiniai', title: 'Gerosios patirties leidiniai', events: 0, participants: 0 },
    { code: 'straipsniai_spaudoje', title: 'Straipsniai spaudoje', events: 0, participants: 0 },
    { code: 'mokytoju_kurybos_vakarai', title: 'Mokytojų kūrybos vakarai', events: 0, participants: 0 },
    { code: 'kiti_renginiai', title: 'Kiti renginiai (parodos, metodinės dienos, forumai, kūrybinės dirbtuvės, atviros pamokos, praktiniai užsiėmimai, stažuotės, konkursas ir kt.)', events: 0, participants: 0 },
    { code: 'kiti_renginiai_2', title: '', events: 0, participants: 0 },
];

const ScGoodPractices: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { goodPractice, isGoodPracticeFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());

    useEffect(() => {
        dispatch(getGoodPractice({ date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null }));
    }, [dispatch, selectedDate]);

    // Set form values when good practice data is loaded
    useEffect(() => {
        if (goodPractice) {
            // Merge default forms with existing data
            const mergedForms = defaultDisseminationForms.map((defaultForm) => {
                const existingForm = goodPractice.dissemination_forms?.find(
                    (f: any) => f.code === defaultForm.code,
                );
                return existingForm || defaultForm;
            });

            // Extract "other" value from good_practice_levels if it exists
            const predefinedLevels = [
                'pedagogine_sistema', 'ugdymo_tikslai', 'ugdymo_turinys', 'ugdymo_metodai',
                'mokytojo_vaidmuo', 'mokinio_veikla', 'mokymosi_motyvacija', 'vertinimas',
                'mokykla', 'organizacijos_kultura', 'mikroklimatas', 'socialine_aplinka',
                'vadyba', 'svietimo_sistema', 'svietimo_politika', 'svietimo_sistemos_struktura',
                'ugdymo_turinys_sistema', 'mokytoju_rengimas_kvalifikacija', 'mokymosi_pasiekimu_vertinimas',
            ];
            const otherValue = goodPractice.good_practice_levels?.find(
                (level: string) => !predefinedLevels.includes(level),
            ) || '';

            form.setFieldsValue({
                is_exchange_regulated: goodPractice.is_exchange_regulated,
                regulation_documents: goodPractice.regulation_documents || '',
                good_practice_topics: goodPractice.good_practice_topics || [],
                good_practice_levels: goodPractice.good_practice_levels?.filter(
                    (level: string) => predefinedLevels.includes(level),
                ) || [],
                good_practice_other: otherValue,
                dissemination_forms: mergedForms.length > 0 ? mergedForms : defaultDisseminationForms,
                dissemination_partners: goodPractice.dissemination_partners || [],
                has_experience_bank: goodPractice.has_experience_bank,
                experience_bank_description: goodPractice.experience_bank_description || '',
                experience_bank_contributors: goodPractice.experience_bank_contributors || [],
                experience_bank_access: goodPractice.experience_bank_access || [],
                experience_bank_use_research: goodPractice.experience_bank_use_research,
                experience_bank_use_methods: goodPractice.experience_bank_use_methods || [],
            });
        } else {
            // Reset to defaults if no data
            form.setFieldsValue({
                is_exchange_regulated: false,
                regulation_documents: '',
                good_practice_topics: [],
                good_practice_levels: [],
                dissemination_forms: defaultDisseminationForms,
                dissemination_partners: [],
                has_experience_bank: false,
                experience_bank_description: '',
                experience_bank_contributors: [],
                experience_bank_access: [],
                experience_bank_use_research: false,
                experience_bank_use_methods: [],
            });
        }
    }, [goodPractice, form]);

    const handleSave = async (values: any) => {
        // Transform dissemination_forms to ensure all required fields are present
        const transformedForms = (values.dissemination_forms || defaultDisseminationForms).map((formItem: any) => ({
            code: formItem.code,
            title: formItem.title,
            events: formItem.events || 0,
            participants: formItem.participants || 0,
        }));

        // Transform experience_bank_media to experience_bank_access if needed
        // For now, we'll keep experience_bank_access as is and handle media separately if needed
        const experienceBankAccess = values.experience_bank_access || [];

        // Combine good_practice_levels with good_practice_other if it has a value
        const goodPracticeLevels = [...(values.good_practice_levels || [])];
        if (values.good_practice_other && values.good_practice_other.trim()) {
            goodPracticeLevels.push(values.good_practice_other.trim());
        }

        const requestData: any = {
            ...(goodPractice?.id && { id: goodPractice.id }),
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
            is_exchange_regulated: values.is_exchange_regulated || false,
            regulation_documents: values.regulation_documents || null,
            good_practice_topics: values.good_practice_topics || [],
            good_practice_levels: goodPracticeLevels,
            dissemination_forms: transformedForms,
            dissemination_partners: values.dissemination_partners || [],
            has_experience_bank: values.has_experience_bank || false,
            experience_bank_description: values.experience_bank_description || null,
            experience_bank_contributors: values.experience_bank_contributors || [],
            experience_bank_access: experienceBankAccess,
            experience_bank_use_research: values.experience_bank_use_research || false,
            experience_bank_use_methods: values.experience_bank_use_methods || [],
        };

        await dispatch(createOrUpdateGoodPractice(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getGoodPractice({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    // Dissemination forms table columns
    const disseminationFormsColumns = [
        {
            title: 'Formos',
            dataIndex: 'title',
            key: 'title',
            width: '50%',
            render: (text: string, record: any, index: number) => {
                if (index === defaultDisseminationForms.length - 1) {
                    // Last row is empty for additional input
                    return (
                        <Form.Item
                            name={['dissemination_forms', index, 'title']}
                            style={{ margin: 0 }}>
                            <Input placeholder="Įrašykite kitą renginio formą" />
                        </Form.Item>
                    );
                }
                if (index === defaultDisseminationForms.length) {
                    // Total row
                    return <Text strong>Iš viso</Text>;
                }
                return text;
            },
        },
        {
            title: 'Renginių skaičius',
            dataIndex: 'events',
            key: 'events',
            width: '25%',
            render: (value: number, record: any, index: number) => {
                if (index === defaultDisseminationForms.length) {
                    // Total row
                    return (
                        <Form.Item shouldUpdate style={{ margin: 0 }}>
                            {({ getFieldValue }) => {
                                const formData = getFieldValue('dissemination_forms') || defaultDisseminationForms;
                                const total = formData.reduce((sum: number, formItem: any) => sum + (Number(formItem?.events) || 0), 0);
                                return <Text strong style={{ fontSize: '16px' }}>{total}</Text>;
                            }}
                        </Form.Item>
                    );
                }
                return (
                    <Form.Item
                        name={['dissemination_forms', index, 'events']}
                        style={{ margin: 0 }}
                        initialValue={0}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                );
            },
        },
        {
            title: 'Dalyvių skaičius',
            dataIndex: 'participants',
            key: 'participants',
            width: '25%',
            render: (value: number, record: any, index: number) => {
                if (index === defaultDisseminationForms.length) {
                    // Total row
                    return (
                        <Form.Item shouldUpdate style={{ margin: 0 }}>
                            {({ getFieldValue }) => {
                                const formData = getFieldValue('dissemination_forms') || defaultDisseminationForms;
                                const total = formData.reduce((sum: number, formItem: any) => sum + (Number(formItem?.participants) || 0), 0);
                                return <Text strong style={{ fontSize: '16px' }}>{total}</Text>;
                            }}
                        </Form.Item>
                    );
                }
                return (
                    <Form.Item
                        name={['dissemination_forms', index, 'participants']}
                        style={{ margin: 0 }}
                        initialValue={0}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                );
            },
        },
    ];

    return (
        <ConfigProvider locale={locale}>
            <div className="school-good-practices">
                <Card title="Gerosios patirties sklaida ir edukacinės patirties bankas" className="good-practices-form-card" loading={isGoodPracticeFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {goodPractice?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {goodPractice.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRefreshClick}
                                loading={!!isGoodPracticeFetching}>
                                Atnaujinti
                            </Button>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            is_exchange_regulated: false,
                            regulation_documents: '',
                            good_practice_topics: [],
                            good_practice_levels: [],
                            good_practice_other: '',
                            dissemination_forms: defaultDisseminationForms,
                            dissemination_partners: [],
                            has_experience_bank: false,
                            has_client_accounting: null,
                            experience_bank_description: '',
                            experience_bank_contributors: [],
                            experience_bank_access: [],
                            experience_bank_media: {
                                paper: 0,
                                electronic: 0,
                                videotapes: 0,
                                other: '',
                            },
                            client_accounting: {
                                total: 0,
                                institution_staff: 0,
                                lecturers: 0,
                                event_participants: 0,
                                region_education_staff: 0,
                                students: 0,
                                other: '',
                            },
                            experience_bank_use_research: false,
                            experience_bank_use_methods: [],
                        }}>

                        {/* 4.1 Ar keitimasis gerąja patirtimi institucijoje yra reglamentuotas dokumentais? */}
                        <Card title="4.1 Ar keitimasis gerąja patirtimi institucijoje yra reglamentuotas dokumentais?" style={{ marginBottom: 16 }}>
                            <Form.Item name="is_exchange_regulated" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.is_exchange_regulated !== currentValues.is_exchange_regulated}>
                                {({ getFieldValue }) => (getFieldValue('is_exchange_regulated') ? (
                                    <Form.Item name="regulation_documents" label="4.1.1 Nurodykite dokumentus">
                                        <TextArea rows={4} placeholder="Įrašykite dokumentų aprašymą" />
                                    </Form.Item>
                                ) : null)}
                            </Form.Item>
                        </Card>

                        {/* 4.2 Keitimosi gerąja patirtimi sritys */}
                        <Card title="4.2 Keitimosi gerąja patirtimi sritys:" style={{ marginBottom: 16 }}>
                            <Form.Item name="good_practice_levels" label="Pasirinkite sritis">
                                <Checkbox.Group>
                                    <Row gutter={[24, 16]}>
                                        <Col span={8}>
                                            <Title level={5}>4.2.1 Pedagoginė sistema</Title>
                                            <Row>
                                                <Col span={24}>
                                                    <Checkbox value="ugdymo_tikslai">4.2.1.1 Ugdymo tikslai</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="ugdymo_turinys">4.2.1.2 Ugdymo turinys</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="ugdymo_metodai">4.2.1.3 Ugdymo metodai</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="mokytojo_vaidmuo">4.2.1.4 Mokytojo vaidmuo</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="mokinio_veikla">4.2.1.5 Mokinio veikla</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="mokymosi_motyvacija">4.2.1.6 Mokymosi motyvacija</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="vertinimas">4.2.1.7 Vertinimas</Checkbox>
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col span={8}>
                                            <Title level={5}>4.2.2 Mokykla</Title>
                                            <Row>
                                                <Col span={24}>
                                                    <Checkbox value="organizacijos_kultura">4.2.2.1 Organizacijos kultūra</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="mikroklimatas">4.2.2.2 Mikroklimatas</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="socialine_aplinka">4.2.2.3 Socialinė aplinka</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="vadyba">4.2.2.4 Vadyba</Checkbox>
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col span={8}>
                                            <Title level={5}>4.2.3 Švietimo sistema</Title>
                                            <Row>
                                                <Col span={24}>
                                                    <Checkbox value="svietimo_politika">4.2.3.1 Švietimo politika</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="svietimo_sistemos_struktura">4.2.3.2 Švietimo sistemos struktūra</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="ugdymo_turinys_sistema">4.2.3.3 Ugdymo turinys</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="mokytoju_rengimas_kvalifikacija">4.2.3.4 Mokytojų rengimas ir kvalifikacijos tobulinimas</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="mokymosi_pasiekimu_vertinimas">4.2.3.5 Mokymosi pasiekimų vertinimas (egzaminai, tarptautiniai tyrimai)</Checkbox>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                            <Row>
                                <Col span={24}>
                                    <Form.Item name="good_practice_other" label="4.2.4 Kita (įrašykite)">
                                        <Input placeholder="Įrašykite kitą sritį" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 4.3 Keitimosi gerąja patirtimi renginių ir dalyvių skaičius */}
                        <Card title="4.3 Keitimosi gerąja patirtimi renginių ir dalyvių skaičius pagal formas per ataskaitinį laikotarpį (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Form.Item name="dissemination_forms" initialValue={defaultDisseminationForms}>
                                <Table
                                    columns={disseminationFormsColumns}
                                    dataSource={[...defaultDisseminationForms, { code: 'total', title: 'Iš viso', events: 0, participants: 0 }]}
                                    pagination={false}
                                    rowKey="code"
                                    bordered
                                    rowClassName={(record) => (record.code === 'total' ? 'total-row' : '')} />
                            </Form.Item>
                        </Card>

                        {/* 4.4 Kaip organizuojamas keitimosi gerąja patirtimi procesas? */}
                        <Card title="4.4 Kaip organizuojamas keitimosi gerąja patirtimi procesas?" style={{ marginBottom: 16 }}>
                            <Form.Item name="dissemination_partners" label="Pasirinkite būdus">
                                <Checkbox.Group>
                                    <Row gutter={[24, 16]}>
                                        <Col span={12}>
                                            <Title level={5}>4.4.1 Skleidžiančiojo patirtį suradimas:</Title>
                                            <Row>
                                                <Col span={24}>
                                                    <Checkbox value="norintieji_kreipiasi">4.4.1.1 Norintieji pasidalyti savo patirtimi kreipiasi į instituciją</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="institucija_iesko">4.4.1.2 Institucija ieško norinčiųjų pasidalyti patirtimi</Checkbox>
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col span={12}>
                                            <Title level={5}>4.4.2 Priimančiojo patirtį suradimas</Title>
                                            <Row>
                                                <Col span={24}>
                                                    <Checkbox value="norintieji_suzinoti_kreipiasi">4.4.2.1 Norintieji sužinoti apie kitų patirtį kreipiasi į instituciją</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="institucija_iesko_suzinoti">4.4.2.2 Institucija ieško norinčiųjų sužinoti apie kitų patirtį</Checkbox>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 4.5 Kaip institucija tiria keitimosi gerąja patirtimi kokybę? */}
                        <Card title="4.5 Kaip institucija tiria keitimosi gerąja patirtimi kokybę?" style={{ marginBottom: 16 }}>
                            <Form.Item name={['experience_bank_use_methods']} label="Kokybės tyrimo metodai">
                                <Checkbox.Group>
                                    <Row>
                                        <Col span={24}>
                                            <Checkbox value="apklausos">4.5.1 Organizuojamos apklausos (anketavimas)</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="neformalus">4.5.2 Neformaliuoju būdu</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="kita_kokybe">4.5.3 Kita (Metodinių būrelių susirinkimų metu - aptarimai; refleksijos po renginio)</Checkbox>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 4.6 Ar kaupiamas edukacinės patirties bankas? */}
                        <Card title="4.6 Ar kaupiamas edukacinės patirties bankas?" style={{ marginBottom: 16 }}>
                            <Form.Item name="has_experience_bank" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Card>

                        {/* 4.7 Kokia laikmenų forma kaupiamas edukacinės patirties bankas? */}
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.has_experience_bank !== currentValues.has_experience_bank}>
                            {({ getFieldValue }) => (getFieldValue('has_experience_bank') ? (
                                <Card title="4.7 Kokia laikmenų forma kaupiamas edukacinės patirties bankas? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                                    <Row gutter={16}>
                                        <Col span={6}>
                                            <Form.Item name={['experience_bank_media', 'paper']} label="4.7.1 Popierinių">
                                                <InputNumber min={0} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={6}>
                                            <Form.Item name={['experience_bank_media', 'electronic']} label="4.7.2 Elektroninių">
                                                <InputNumber min={0} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={6}>
                                            <Form.Item name={['experience_bank_media', 'videotapes']} label="4.7.3 Vaizdajuosčių">
                                                <InputNumber min={0} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={6}>
                                            <Form.Item name={['experience_bank_media', 'other']} label="4.7.4 Kita (įrašykite)">
                                                <Input placeholder="pvz. internetinė saugykla" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Card>
                            ) : null)}
                        </Form.Item>

                        {/* 4.8 Ar vedama klientų apskaita? */}
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.has_experience_bank !== currentValues.has_experience_bank}>
                            {({ getFieldValue }) => (getFieldValue('has_experience_bank') ? (
                                <Card title="4.8 Ar vedama klientų, besinaudojančių edukacinės patirties banko paslaugomis, apskaita?" style={{ marginBottom: 16 }}>
                                    <Form.Item name="has_client_accounting" label="Klientų apskaita">
                                        <Radio.Group>
                                            <Radio value="taip">4.8.1 Taip</Radio>
                                            <Radio value="ne">4.8.2 Ne (pereikite prie 4.10 klausimo)</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Card>
                            ) : null)}
                        </Form.Item>

                        {/* 4.9 Kiek klientų pasinaudojo edukacinės patirties banku? */}
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => (
                                prevValues.has_experience_bank !== currentValues.has_experience_bank
                                || prevValues.has_client_accounting !== currentValues.has_client_accounting
                            )}>
                            {({ getFieldValue }) => {
                                const hasExperienceBank = getFieldValue('has_experience_bank');
                                const hasClientAccounting = getFieldValue('has_client_accounting');
                                return (hasExperienceBank && hasClientAccounting === 'taip') ? (
                                    <Card title="4.9 Kiek klientų pasinaudojo edukacinės patirties banku per ataskaitinį laikotarpį? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name={['client_accounting', 'total']} label="4.9.1 Iš viso">
                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Divider />
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name={['client_accounting', 'institution_staff']} label="4.9.2 Institucijos darbuotojų">
                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name={['client_accounting', 'lecturers']} label="4.9.3 Lektorių">
                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name={['client_accounting', 'event_participants']} label="4.9.4 Institucijos renginių dalyvių">
                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name={['client_accounting', 'region_education_staff']} label="4.9.5 Regiono švietimo darbuotojų">
                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name={['client_accounting', 'students']} label="4.9.6 Mokinių">
                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name={['client_accounting', 'other']} label="4.9.7 Kitų (įrašykite)">
                                                    <Input placeholder="Įrašykite kitą grupę" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                ) : null;
                            }}
                        </Form.Item>

                        {/* 4.10 Kas institucijoje vykdo gerosios ir/ar pažangiausiosios patirties atranką? */}
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.has_experience_bank !== currentValues.has_experience_bank}>
                            {({ getFieldValue }) => (getFieldValue('has_experience_bank') ? (
                                <Card title="4.10 Kas institucijoje vykdo gerosios ir/ar pažangiausiosios patirties atranką edukacinės patirties bankui?" style={{ marginBottom: 16 }}>
                                    <Form.Item name={['experience_bank_contributors']} label="Atranką vykdo">
                                        <Checkbox.Group>
                                            <Row>
                                                <Col span={24}>
                                                    <Checkbox value="institucijos_darbuotojai">4.10.1 Institucijos darbuotojai</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="mokytojai_praktikai">4.10.2 Mokytojai praktikai</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="mokslininkai">4.10.3 Mokslininkai</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="nepriklausomi_ekspertai">4.10.4 Nepriklausomi ekspertai</Checkbox>
                                                </Col>
                                                <Col span={24}>
                                                    <Checkbox value="kita_atranka">4.10.5 Kita (įrašykite)</Checkbox>
                                                </Col>
                                            </Row>
                                        </Checkbox.Group>
                                    </Form.Item>
                                </Card>
                            ) : null)}
                        </Form.Item>

                        {/* 4.11 Ar tiriate, kaip diegiama edukacinės patirties banke sukaupta patirtis? */}
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.has_experience_bank !== currentValues.has_experience_bank}>
                            {({ getFieldValue: getFieldValueOuter }) => (getFieldValueOuter('has_experience_bank') ? (
                                <Card title="4.11 Ar tiriate, kaip diegiama edukacinės patirties banke sukaupta patirtis?" style={{ marginBottom: 16 }}>
                                    <Form.Item name="experience_bank_use_research" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(prevValues, currentValues) => prevValues.experience_bank_use_research !== currentValues.experience_bank_use_research}>
                                        {({ getFieldValue: getFieldValueInner }) => (getFieldValueInner('experience_bank_use_research') ? (
                                            <Form.Item name="experience_bank_description" label="Aprašymas">
                                                <TextArea rows={4} placeholder="Anketinių apklausų metu, neformaliuose pokalbiuose su renginių dalyviais ir pan." />
                                            </Form.Item>
                                        ) : null)}
                                    </Form.Item>
                                </Card>
                            ) : null)}
                        </Form.Item>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScGoodPractices;
